import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import LessonDocumentView from '@/features/connectlessen/LessonDocumentView';
import { fetchSharedLesson } from '@/features/connectlessen/share/shareApi';

const setMeta = (name, content) => {
  const element = document.createElement('meta');
  element.name = name;
  element.content = content;
  document.head.appendChild(element);
  return () => element.remove();
};

const Notice = ({ title, text }) => (
  <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="share-notice">
    <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
    <p className="mt-2 text-sm text-slate-600">{text}</p>
  </div>
);

// Publieke, niet-geïndexeerde weergave van één gepubliceerde connectles via een geheime link.
const PublicConnectlesPage = () => {
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const removers = [setMeta('robots', 'noindex, nofollow, noarchive'), setMeta('referrer', 'no-referrer')];
    return () => removers.forEach((remove) => remove());
  }, []);

  const load = useCallback(async (accessCode) => {
    setBusy(true);
    try {
      setResult(await fetchSharedLesson(token, accessCode));
    } catch (error) {
      setResult({ status: 'error' });
    } finally {
      setBusy(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // De browser drukt de paginatitel als koptekst af: toon de lestitel in plaats van de app-naam.
  const lessonTitle = result?.status === 'ready' ? result.lesson?.title : null;
  useEffect(() => {
    if (!lessonTitle) return undefined;
    const previous = document.title;
    document.title = lessonTitle;
    return () => { document.title = previous; };
  }, [lessonTitle]);

  if (!result) return <div className="mx-auto h-40 max-w-3xl animate-pulse rounded-xl bg-slate-200" aria-busy="true" />;
  if (result.status === 'invalid') return <Notice title="Deze link is niet (meer) geldig" text="De link is ingetrokken, verlopen of de les is niet meer beschikbaar. Vraag een nieuwe link aan degene die hem deelde." />;
  if (result.status === 'limited') return <Notice title="Even geduld" text={result.message || 'Er zijn te veel pogingen gedaan. Probeer het later opnieuw.'} />;
  if (result.status === 'error') return <Notice title="De les kon niet worden geladen" text="Probeer het later opnieuw." />;

  if (result.status === 'code') {
    return (
      <form
        className="mx-auto max-w-sm space-y-4 px-4 py-20"
        onSubmit={(event) => { event.preventDefault(); if (code) load(code); }}
        data-testid="share-code-form"
      >
        <div className="text-center">
          <Lock className="mx-auto mb-2 h-8 w-8 text-slate-400" />
          <h1 className="text-xl font-semibold text-slate-900">Toegangscode nodig</h1>
          <p className="mt-1 text-sm text-slate-600">Voer de code in die je bij deze link hebt gekregen.</p>
        </div>
        <Input aria-label="Toegangscode" type="password" autoComplete="off" autoFocus value={code} onChange={(event) => setCode(event.target.value)} />
        {result.wrongCode && <p role="alert" className="text-sm text-red-600">{result.message || 'Onjuiste toegangscode'}</p>}
        <Button type="submit" disabled={busy || !code} className="w-full bg-fuchsia-600 text-white hover:bg-fuchsia-700">Bekijk de les</Button>
      </form>
    );
  }

  return (
    <main className="lesson-print-root mx-auto max-w-3xl bg-white px-4 py-6 sm:py-10 print:max-w-none print:p-0">
      <style>{'@media print { @page { size: A4; margin: 12.7mm; } }'}</style>
      <div className="mb-4 flex flex-col items-end gap-1 print:hidden">
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />Afdrukken</Button>
        <p className="max-w-xs text-right text-xs text-slate-500" data-testid="print-hint">
          Voor een schone A4-afdruk of pdf: kies in het printvenster &ldquo;Opslaan als pdf&rdquo; en zet &ldquo;Kop- en voettekst&rdquo; uit.
        </p>
      </div>
      <LessonDocumentView lesson={result.lesson} organization={result.organization} showHeader />
    </main>
  );
};

export default PublicConnectlesPage;
